import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import {
  Menu, X, BookOpen, GraduationCap, Terminal, Brain, ArrowRight, MessageSquare
} from "lucide-react";
import { getBootcampStatus } from "../lib/bootcamp";

const NAV_LINKS = [
  { path: "/tutorials",   label: "Tutorials",      icon: BookOpen,      badge: null                              },
  { path: "/bootcamp",    label: "Bootcamp",        icon: GraduationCap, badge: null                              },
  { path: "/playground",  label: "Code Simulator",  icon: Terminal,      badge: null                              },
  { path: "/ai-interview",label: "AI Interview",    icon: Brain,         badge: { text: "Soon", color: "amber" } },
  { path: "/contact",     label: "Contact",         icon: MessageSquare, badge: null                              },
];

const LogoIcon = () => (
  <div className="w-8 h-8 bg-[#00E676] flex items-center justify-center flex-shrink-0">
    <Terminal className="w-5 h-5 text-[#0A0F1E]" />
  </div>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const [bootcampStatus, setBootcampStatus] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    
    // Fetch bootcamp status
    getBootcampStatus().then(data => setBootcampStatus(data));

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const monoFont = { fontFamily: "'JetBrains Mono', monospace" };

  return (
    <>
      <nav
        data-testid="navbar"
        style={{
          backgroundColor: scrolled || isOpen ? '#0A0F1E' : 'transparent',
          borderBottom: scrolled || isOpen ? '1px solid #1F2937' : '1px solid transparent',
        }}
        className="fixed top-0 left-0 right-0 z-[60] transition-all duration-300"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0 z-[70]">
              <LogoIcon />
              <span style={monoFont} className="font-bold text-[15px] text-white tracking-tight group-hover:text-[#00E676] transition-colors">
                DevHire<span className="text-[#00E676]">Lab</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  style={monoFont}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-none text-[12px] font-bold transition-all duration-200 ${
                    isActive(link.path)
                      ? "text-[#00E676] bg-[#00E676]/5"
                      : "text-[#6B7280] hover:text-white hover:bg-white/5"
                  }`}
                >
                  {isActive(link.path) && (
                    <motion.div
                      layoutId="nav-active"
                      className="absolute -bottom-[1px] left-0 right-0 h-[1px] bg-[#00E676]"
                    />
                  )}
                  <link.icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {link.label}
                  {link.path === '/bootcamp' && bootcampStatus && (
                    <div 
                      className="w-1.5 h-1.5 rounded-full animate-pulse" 
                      style={{ 
                        backgroundColor: bootcampStatus.status === 'enrolling' ? '#00E676' : 
                                        bootcampStatus.status === 'in_progress' ? '#3B82F6' :
                                        bootcampStatus.status === 'completed' ? '#6B7280' : '#F59E0B'
                      }} 
                    />
                  )}
                  {link.badge && (
                    <span className={`px-1.5 py-0.5 rounded-none text-[8px] font-black uppercase ${link.badge.color === 'amber' ? 'bg-[#F59E0B] text-[#0A0F1E]' : 'bg-[#00E676] text-[#0A0F1E]'}`}>
                      {link.badge.text}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/bootcamp">
                <Button size="sm" variant="outline" className="border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-[#0A0F1E]">
                  Join Bootcamp
                </Button>
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              className="md:hidden z-[70] p-2 -mr-2 rounded-none text-slate-400 hover:text-white transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{ backgroundColor: '#0A0F1E' }}
            className="fixed inset-0 z-[55] md:hidden flex flex-col p-6 pt-24"
          >
            <div className="flex flex-col gap-2 relative z-10">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={link.path}
                    style={monoFont}
                    className={`flex items-center justify-between p-5 rounded-none border transition-all ${
                      isActive(link.path)
                        ? "bg-[#00E676]/10 border-[#00E676]/20 text-[#00E676]"
                        : "bg-white/5 border-white/5 text-[#6B7280]"
                    }`}
                  >
                    <div className="flex items-center gap-4 text-lg font-black uppercase tracking-tight">
                      <link.icon className="w-6 h-6" />
                      {link.label}
                    </div>
                    <ArrowRight className="w-5 h-5 opacity-40" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-auto flex flex-col gap-4 pb-8"
            >
              <Link to="/bootcamp" className="w-full">
                <Button className="w-full h-16 rounded-none bg-[#00E676] text-[#0A0F1E] font-black text-xl">
                  JOIN BOOTCAMP
                </Button>
              </Link>
              <p style={monoFont} className="text-center text-[10px] text-[#6B7280] uppercase tracking-[0.3em]">
                Your Python Journey Starts Here
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
